import { type ReactElement, useMemo, useState } from 'react';
import { type AnalyticsBiQueryView } from '@vello/api-client';
import {
  type BiTarget,
  defaultLowerIsBetter,
  getMetric,
  vetTarget,
} from '@vello/analytics-semantic';
import { 
  BarList,
  BulletGauge,
  Button,
  ColumnChart,
  ComboChart,
  CohortGrid,
  DataTable,
  Field,
  Funnel,
  GeoMap,
  HeatmapGrid,
  Histogram,
  Notice,
  Panel,
  SectionTitle,
  Select,
  StackedAreaChart,
  StackedBarChart,
  TextInput,
  Waterfall,
  type ChartExportLabels,
  type DataTableColumn,
  type DataTableRow, Text, Grid, Stack, Row, Box, useColorScheme } from '@vello/web-theme';
import {
  type BiInsightDef,
  type BiUnit,
  type CrossFilterSelect,
  type InsightEnv,
  type InsightModel,
  type InsightSeriesGroup,
  type InsightSeriesModel,
  type InsightViews,
  type ScoreItem,
  type TableModel,
} from '../../../src/index';
import { basemapStyleUrl } from '@vello/maps-ui';
import { useT } from '../../i18n/provider';
import { useAdmin } from '../../session';
import { type BiText, BiProblem } from './bi-ui';
import { type Loadable, type TargetsApi } from './bi-hooks';

/** What an insight needs from the screen. */
export interface InsightContext {
  readonly text: BiText;
  readonly env: InsightEnv;
  /** The values the bar is filtered to on a dimension (so their marks read as selected). */
  readonly selectedOf: (dimension: string) => readonly string[];
  /** A click on a mark filters the screen (the D528 contract). */
  readonly onSelect: CrossFilterSelect;
  readonly targets: TargetsApi;
  readonly onRetry: () => void;
}

/** The export words of every chart. */
export function useExportLabels(): ChartExportLabels {
  const { t } = useT();
  return useMemo(() => ({ menu: t('bi.chart.export'), png: t('bi.chart.exportPng'), csv: t('bi.chart.exportCsv') }), [t]);
}

// ───────────────────────────── the card ─────────────────────────────

/** One insight: its title and rule, the chart (or its loading / error), the way down to the rows. */
export function InsightCard({ def, tiles, ctx, onDrill }: { def: BiInsightDef; tiles: Readonly<Record<string, Loadable<AnalyticsBiQueryView>>>; ctx: InsightContext; onDrill: () => void }): ReactElement {
  const { t } = ctx.text;
  const mine = def.blocks.map((b) => tiles[`ins:${def.id}:${b.id}`]);
  const failed = mine.find((x) => x?.status === 'error');
  const loading = mine.length === 0 || mine.some((x) => x === undefined || x.status === 'loading');
  let body: ReactElement;
  if (failed?.status === 'error') {
    body = <BiProblem error={failed.error} onRetry={ctx.onRetry} text={ctx.text} />;
  } else if (loading) {
    body = <BarList items={[]} loading loadingLabel={t('ui.loading')} />;
  } else {
    const views: Record<string, AnalyticsBiQueryView> = {};
    def.blocks.forEach((b, i) => {
      const x = mine[i];
      if (x?.status === 'ready') views[b.id] = x.value;
    });
    const model = def.build(views as InsightViews, ctx.env);
    body = <InsightBody def={def} model={model} views={views} ctx={ctx} />;
  }
  return (
    <div data-insight={def.id} data-span={def.wide === true ? 'all' : 'one'}>
      <Panel><Stack gap={3}>
        <SectionTitle level={3} size="sub">{t(def.titleId)}</SectionTitle>
        {def.noteId !== undefined && <Text as="p" role="caption" tone="secondary">{t(def.noteId)}</Text>}
        {body}
        <div>
          <Button variant="ghost" icon="arrowRight" onClick={onDrill}>
            {t('bi.ins.drill')}
          </Button>
        </div>
      </Stack></Panel>
    </div>
  );
}

function InsightBody({ def, model, views, ctx }: { def: BiInsightDef; model: InsightModel; views: Record<string, AnalyticsBiQueryView>; ctx: InsightContext }): ReactElement {
  switch (model.kind) {
    case 'scorecard':
      return <Scorecard model={model} ctx={ctx} />;
    case 'heatmap':
      return <Heat model={model} title={ctx.text.t(def.titleId)} ctx={ctx} />;
    case 'histogram':
      return <Distribution model={model} title={ctx.text.t(def.titleId)} ctx={ctx} />;
    case 'funnel':
      return <FunnelView model={model} title={ctx.text.t(def.titleId)} ctx={ctx} />;
    case 'cohort':
      return <Cohort model={model} title={ctx.text.t(def.titleId)} ctx={ctx} />;
    case 'waterfall':
      return <Bridge model={model} title={ctx.text.t(def.titleId)} ctx={ctx} />;
    case 'geo':
      return <PickupMap model={model} title={ctx.text.t(def.titleId)} ctx={ctx} />;
    case 'table':
      return <Rows model={model} title={ctx.text.t(def.titleId)} ctx={ctx} />;
    default:
      return <SeriesView model={model} title={ctx.text.t(def.titleId)} ctx={ctx} views={views} />;
  }
}

// ───────────────────────────── scorecard + goals ─────────────────────────────

/** The bullet's ranges: the goal's accepted band when it has one (ascending limits to the end of the axis). */
function gaugeScale(item: ScoreItem): { min?: number; max: number; bands?: number[] } {
  const target = item.target;
  const value = item.value ?? 0;
  if (item.unit === 'ratio') return { min: 0, max: 1, ...(target?.bandLow != null && target.bandHigh != null ? { bands: [target.bandLow, target.bandHigh, 1].filter((v, i, a) => i === 0 || v > (a[i - 1] as number)) } : {}) };
  const top = Math.max(value, target?.value ?? 0, target?.bandHigh ?? 0) * 1.25 || 1;
  const bands = target?.bandLow != null && target.bandHigh != null ? [target.bandLow, target.bandHigh, top].filter((v, i, a) => i === 0 || v > (a[i - 1] as number)) : undefined;
  return { max: top, ...(item.unit === 'delta_s' ? { min: Math.min(0, value, target?.bandLow ?? 0, target?.value ?? 0) * 1.25 } : {}), ...(bands !== undefined ? { bands } : {}) };
}

function Scorecard({ model, ctx }: { model: Extract<InsightModel, { kind: 'scorecard' }>; ctx: InsightContext }): ReactElement {
  const { t, fmt } = ctx.text;
  const labels = useExportLabels();
  return (
    <Grid min="md" gap={5}>
      {model.items.map((item) => {
        const scale = gaugeScale(item);
        const unit = item.unit as BiUnit;
        const format = (n: number): string => fmt.chartText(unit, n, undefined);
        return (
          <Stack key={item.metric} gap={2} data-score={item.metric}>
            {item.value === null ? (
              <>
                <strong>{ctx.text.metricName(item.metric)}</strong>
                <Text role="caption" tone="secondary">{t('bi.kpi.unavailable')}</Text>
              </>
            ) : (
              <BulletGauge
                label={ctx.text.metricName(item.metric)}
                value={item.value}
                {...(item.target !== null ? { target: item.target.value } : {})}
                {...(scale.min !== undefined ? { min: scale.min } : {})}
                max={scale.max}
                {...(scale.bands !== undefined ? { bands: scale.bands } : {})}
                {...(item.target !== null ? { lowerIsBetter: item.target.lowerIsBetter } : {})}
                reachedLabel={t('bi.target.reached')}
                missedLabel={t('bi.target.missed')}
                targetLabel={t('bi.target.label')}
                formatValue={format}
                exportLabels={labels}
                exportName={item.metric}
                emptyLabel={t('bi.chart.empty')}
              />
            )}
            <GoalLine item={item} marketId={model.marketId} ctx={ctx} />
          </Stack>
        );
      })}
    </Grid>
  );
}

/** Under a gauge: the goal in words (or why there is none), and for an administrator the way to set it. */
function GoalLine({ item, marketId, ctx }: { item: ScoreItem; marketId: string | null; ctx: InsightContext }): ReactElement {
  const { t, fmt } = ctx.text;
  const [editing, setEditing] = useState(false);
  const unit = item.unit as BiUnit;
  if (marketId === null) return <Text role="caption" tone="secondary">{t('bi.target.pickMarket')}</Text>;
  return (
    <Stack gap={2}>
      {item.target === null ? (
        <Text role="caption" tone="secondary">{t('bi.target.none', { market: ctx.text.marketName(marketId) })}</Text>
      ) : (
        <Text role="caption" tone="secondary">
          {t('bi.target.goal', { value: fmt.chartText(unit, item.target.value, undefined), direction: t(item.target.lowerIsBetter ? 'bi.target.lower' : 'bi.target.higher') })}
          {item.target.bandLow !== null && item.target.bandHigh !== null ? ` · ${t('bi.target.band', { low: fmt.chartText(unit, item.target.bandLow, undefined), high: fmt.chartText(unit, item.target.bandHigh, undefined) })}` : ''}
          {item.status === 'near' ? ` · ${t('bi.target.near')}` : ''}
        </Text>
      )}
      {ctx.targets.canManage && !editing && (
        <div>
          <Button variant="ghost" icon="pencil" onClick={() => setEditing(true)}>
            {t(item.target === null ? 'bi.target.set' : 'bi.target.edit')}
          </Button>
        </div>
      )}
      {editing && <TargetEditor metric={item.metric} marketId={marketId} current={item.target} ctx={ctx} onClose={() => setEditing(false)} />}
    </Stack>
  );
}

/** The goal as the form shows it: a share in percent, everything else in the metric's own unit. */
const scaleOf = (unit: string): number => (unit === 'ratio' ? 100 : 1);
const toInput = (n: number | null, unit: string): string => (n === null ? '' : String(Math.round(n * scaleOf(unit) * 1e6) / 1e6));

function TargetEditor({ metric, marketId, current, ctx, onClose }: { metric: string; marketId: string; current: BiTarget | null; ctx: InsightContext; onClose: () => void }): ReactElement {
  const { t } = ctx.text;
  const unit = getMetric(metric)?.unit ?? 'count';
  const [value, setValue] = useState(toInput(current?.value ?? null, unit));
  const [low, setLow] = useState(toInput(current?.bandLow ?? null, unit));
  const [high, setHigh] = useState(toInput(current?.bandHigh ?? null, unit));
  const [lower, setLower] = useState(current?.lowerIsBetter ?? defaultLowerIsBetter(metric));
  const [problem, setProblem] = useState<Parameters<typeof BiProblem>[0]['error'] | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const number = (s: string): number | undefined => (s.trim() === '' || !Number.isFinite(Number(s)) ? undefined : Number(s) / scaleOf(unit));
  const body = { metric, marketId, value: number(value), ...(number(low) !== undefined && number(high) !== undefined ? { bandLow: number(low), bandHigh: number(high) } : {}), lowerIsBetter: lower };
  const vetted = vetTarget(body);
  const id = `target-${metric}`;

  const save = async (): Promise<void> => {
    if (!vetted.ok) return;
    setBusy(true);
    const res = await ctx.targets.set({ metric, marketId, value: vetted.value.value, ...(vetted.value.bandLow !== null ? { bandLow: vetted.value.bandLow, bandHigh: vetted.value.bandHigh } : {}), lowerIsBetter: vetted.value.lowerIsBetter });
    setBusy(false);
    if (res.ok) onClose();
    else setProblem(res.error);
  };
  const clear = async (): Promise<void> => {
    setBusy(true);
    const res = await ctx.targets.clear(metric, marketId);
    setBusy(false);
    if (res.ok) onClose();
    else setProblem(res.error);
  };
  const suffix = unit === 'ratio' ? '%' : unit === 'duration_s' || unit === 'delta_s' ? t('bi.target.seconds') : '';
  return (
    <Box role="group" aria-label={t('bi.target.editorFor', { metric: ctx.text.metricName(metric) })} gap={3} pad={3}>
      <Field label={`${t('bi.target.value')}${suffix !== '' ? ` (${suffix})` : ''}`} htmlFor={`${id}-value`} {...(value !== '' && !vetted.ok ? { error: t('bi.targets.err.value') } : {})}>
        <TextInput id={`${id}-value`} type="number" inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} />
      </Field>
      <Row gap={3} align="stretch" wrap>
        <Field label={`${t('bi.target.low')}${suffix !== '' ? ` (${suffix})` : ''}`} htmlFor={`${id}-low`}>
          <TextInput id={`${id}-low`} type="number" inputMode="decimal" value={low} onChange={(e) => setLow(e.target.value)} />
        </Field>
        <Field label={`${t('bi.target.high')}${suffix !== '' ? ` (${suffix})` : ''}`} htmlFor={`${id}-high`} {...(value !== '' && (low !== '' || high !== '') && !vetted.ok ? { error: t('bi.targets.err.band') } : {})}>
          <TextInput id={`${id}-high`} type="number" inputMode="decimal" value={high} onChange={(e) => setHigh(e.target.value)} />
        </Field>
      </Row>
      <Field label={t('bi.target.direction')} htmlFor={`${id}-dir`}>
        <Select id={`${id}-dir`} value={lower ? 'lower' : 'higher'} onChange={(e) => setLower(e.target.value === 'lower')}>
          <option value="higher">{t('bi.target.higherIsBetter')}</option>
          <option value="lower">{t('bi.target.lowerIsBetter')}</option>
        </Select>
      </Field>
      {problem !== undefined && <BiProblem error={problem} text={ctx.text} />}
      <Row gap={2} align="stretch" wrap>
        <Button variant="primary" disabled={!vetted.ok || busy} onClick={() => void save()}>
          {t('bi.target.save')}
        </Button>
        {current !== null && (
          <Button variant="ghost" disabled={busy} onClick={() => void clear()}>
            {t('bi.target.clear')}
          </Button>
        )}
        <Button variant="ghost" disabled={busy} onClick={onClose}>
          {t('ui.cancel')}
        </Button>
      </Row>
    </Box>
  );
}

// ───────────────────────────── the charts ─────────────────────────────

function Heat({ model, title, ctx }: { model: Extract<InsightModel, { kind: 'heatmap' }>; title: string; ctx: InsightContext }): ReactElement {
  const { t, fmt } = ctx.text;
  const labels = useExportLabels();
  const unit = model.unit as BiUnit;
  return (
    <HeatmapGrid
      title={title}
      rowLabels={model.rowKeys.map((k) => ctx.text.valueText(model.rowDimension, k))}
      colLabels={model.colKeys.map((k) => ctx.text.valueText(model.colDimension, k))}
      rowKeys={model.rowKeys}
      colKeys={model.colKeys}
      rowDimension={model.rowDimension}
      colDimension={model.colDimension}
      values={model.values}
      formatValue={(n) => fmt.chartText(unit, n, undefined)}
      formatCell={(n) => (unit === 'count' ? fmt.count(n) : fmt.chartText(unit, n, undefined))}
      summaryWords={{ highest: t('bi.chart.highest'), lowest: t('bi.chart.lowest'), empty: t('bi.chart.empty') }}
      exportLabels={labels}
      exportName={model.metric}
      emptyLabel={t('bi.chart.empty')}
      onSelect={(s) => ctx.onSelect(s.dimension, s.value)}
      selected={[...ctx.selectedOf(model.rowDimension), ...ctx.selectedOf(model.colDimension)]}
    />
  );
}

function Distribution({ model, title, ctx }: { model: Extract<InsightModel, { kind: 'histogram' }>; title: string; ctx: InsightContext }): ReactElement {
  const { t, fmt } = ctx.text;
  const labels = useExportLabels();
  const edge = (n: number): string => fmt.chartText(model.unit, n, undefined);
  const about = (p: { value: number } | undefined): string => (p === undefined ? t('ui.unavailable') : t('bi.ins.about', { value: edge(p.value) }));
  const markers = [
    ...(model.p50 !== undefined ? [{ value: model.p50.value, label: t('bi.ins.p50', { value: edge(model.p50.value) }) }] : []),
    ...(model.p90 !== undefined ? [{ value: model.p90.value, label: t('bi.ins.p90', { value: edge(model.p90.value) }) }] : []),
    ...(model.zeroMarker ? [{ value: 0, label: t('bi.ins.promised') }] : []),
  ];
  return (
    <Stack gap={2}>
      <Histogram
        title={title}
        bins={model.bins.map((b) => ({ from: b.from, to: b.to, count: b.count }))}
        cumulative
        countLabel={ctx.text.metricName(model.metric)}
        cumulativeLabel={t('bi.chart.cumulative')}
        markers={markers}
        formatValue={(n) => fmt.count(n)}
        formatEdge={edge}
        formatShare={(f) => fmt.percent(f)}
        dimension={model.dimension}
        exportLabels={labels}
        exportName={model.dimension}
        emptyLabel={t('bi.chart.empty')}
        onSelect={(s) => {
          const bin = model.bins.find((b) => `${b.from}..${b.to}` === s.value);
          if (bin !== undefined) ctx.onSelect(model.dimension, bin.key);
        }}
        selected={ctx.selectedOf(model.dimension).flatMap((k) => model.bins.filter((b) => b.key === k).map((b) => `${b.from}..${b.to}`))}
      />
      <Text as="p" role="caption" tone="secondary">
        {t('bi.ins.percentileLine', { p50: about(model.p50), p90: about(model.p90), missing: fmt.count(model.missing) })}
      </Text>
    </Stack>
  );
}

function FunnelView({ model, title, ctx }: { model: Extract<InsightModel, { kind: 'funnel' }>; title: string; ctx: InsightContext }): ReactElement {
  const { t, fmt } = ctx.text;
  const labels = useExportLabels();
  return (
    <Funnel
      title={title}
      steps={model.steps.map((s) => ({ key: s.key, label: t(`bi.ins.funnel.${s.key}`), value: s.value }))}
      words={{ fromPrevious: t('bi.ins.funnel.fromPrevious'), fromFirst: t('bi.ins.funnel.fromFirst'), dropOff: t('bi.ins.funnel.dropOff'), overall: t('bi.ins.funnel.overall') }}
      formatValue={(n) => fmt.count(n)}
      formatShare={(f) => fmt.percent(f)}
      exportLabels={labels}
      exportName="funnel"
      emptyLabel={t('bi.chart.empty')}
    />
  );
}

function Cohort({ model, title, ctx }: { model: Extract<InsightModel, { kind: 'cohort' }>; title: string; ctx: InsightContext }): ReactElement {
  const { t, fmt } = ctx.text;
  const labels = useExportLabels();
  const label = (month: string): string => ctx.text.valueText('cohort_month', month);
  const byLabel = new Map(model.cohorts.map((c) => [label(c.cohort), c.cohort]));
  return (
    <Stack gap={2}>
      <CohortGrid
        title={title}
        cohorts={model.cohorts.map((c) => ({ cohort: label(c.cohort), size: c.size, retained: [...c.retained] }))}
        periodLabel={(p) => `M${p}`}
        sizeLabel={t('bi.ins.cohortSize')}
        averageLabel={t('bi.ins.cohortAverage')}
        pendingLabel={t('bi.ins.cohortPending')}
        formatValue={(n) => fmt.count(n)}
        formatRate={(f) => fmt.percent(f)}
        dimension="cohort_month"
        exportLabels={labels}
        exportName="cohort"
        emptyLabel={t('bi.chart.empty')}
        onSelect={(s) => {
          const month = byLabel.get(s.value);
          if (month !== undefined) ctx.onSelect('cohort_month', month);
        }}
        selected={ctx.selectedOf('cohort_month').map(label)}
      />
      {model.skipped > 0 && <Text as="p" role="caption" tone="secondary">{t('bi.ins.cohortSkipped', { n: model.skipped })}</Text>}
    </Stack>
  );
}

function Bridge({ model, title, ctx }: { model: Extract<InsightModel, { kind: 'waterfall' }>; title: string; ctx: InsightContext }): ReactElement {
  const { t, fmt } = ctx.text;
  const labels = useExportLabels();
  if (model.groups.length === 0) return <Notice tone="neutral">{t('bi.chart.empty')}</Notice>;
  return (
    <Stack gap={4}>
      {model.groups.map((g) => (
        <Waterfall
          key={g.currency ?? 'all'}
          title={g.currency !== undefined ? `${title} · ${g.currency}` : title}
          steps={g.steps.map((s) => ({ key: s.key, label: ctx.text.metricName(s.metric), value: s.value, kind: s.total ? ('total' as const) : ('delta' as const) }))}
          formatValue={(n) => fmt.chartText('money', Math.round(n * 100), g.currency)}
          formatDelta={(n) => `${n < 0 ? '−' : '+'}${fmt.chartText('money', Math.round(Math.abs(n) * 100), g.currency)}`}
          mismatchLabel={t('bi.ins.waterfall.mismatch')}
          exportLabels={labels}
          exportName={`${title}${g.currency !== undefined ? `-${g.currency}` : ''}`}
          emptyLabel={t('bi.chart.empty')}
        />
      ))}
    </Stack>
  );
}

function PickupMap({ model, title, ctx }: { model: Extract<InsightModel, { kind: 'geo' }>; title: string; ctx: InsightContext }): ReactElement {
  const { t, fmt } = ctx.text;
  const labels = useExportLabels();
  const { runtime } = useAdmin();
  const scheme = useColorScheme();
  return (
    <Stack gap={2}>
      <GeoMap
        title={title}
        basemapStyleUrl={basemapStyleUrl(runtime.apiBaseUrl, scheme)}
        cells={model.cells.map((c, i) => ({ key: c.key, label: t('bi.ins.cellLabel', { n: i + 1 }), value: c.value, bounds: { south: c.south, north: c.north, west: c.west, east: c.east } }))}
        valueLabel={ctx.text.metricName(model.metric)}
        height={340}
        formatValue={(n) => fmt.count(n)}
        dimension="pickup_cell"
        exportLabels={labels}
        exportName="demand-by-cell"
        emptyLabel={t('bi.ins.mapEmpty')}
        onSelect={(s) => ctx.onSelect('pickup_cell', s.value)}
        selected={ctx.selectedOf('pickup_cell')}
      />
      {model.dropped > 0 && <Text as="p" role="caption" tone="secondary">{t('bi.ins.mapDropped', { n: model.dropped })}</Text>}
    </Stack>
  );
}

// ───────────────────────────── series ─────────────────────────────

function SeriesView({ model, title, ctx, views }: { model: InsightSeriesModel; title: string; ctx: InsightContext; views: Record<string, AnalyticsBiQueryView> }): ReactElement {
  const { t } = ctx.text;
  void views;
  if (model.groups.length === 0) return <Notice tone="neutral">{t('bi.chart.empty')}</Notice>;
  return (
    <Stack gap={4}>
      {model.groups.map((g) => (
        <SeriesGroupChart key={g.currency ?? 'all'} model={model} group={g} title={g.currency !== undefined && model.groups.length > 1 ? `${title} · ${g.currency}` : title} ctx={ctx} />
      ))}
    </Stack>
  );
}

function SeriesGroupChart({ model, group, title, ctx }: { model: InsightSeriesModel; group: InsightSeriesGroup; title: string; ctx: InsightContext }): ReactElement {
  const { t, fmt } = ctx.text;
  const labels = useExportLabels();
  const unitOf = (metric: string | null): BiUnit => ((metric === null ? model.unit : (getMetric(metric)?.unit ?? 'count')) as BiUnit);
  const format = (n: number, unit: BiUnit = model.unit as BiUnit): string => fmt.chartText(unit, n, group.currency);
  const categories = group.categories.map((k) => ctx.text.valueText(model.dimension, k));
  const keyOf = new Map(categories.map((label, i) => [label, group.categories[i] as string]));
  const seriesName = (s: { key: string; metric: string | null }): string => (s.metric !== null ? ctx.text.metricName(s.metric) : ctx.text.valueText(model.seriesDimension ?? '', s.key));
  const common = {
    title,
    exportLabels: labels,
    exportName: model.dimension,
    emptyLabel: t('bi.chart.empty'),
    dimension: model.dimension,
    onSelect: (s: { value: string }) => {
      const key = keyOf.get(s.value);
      if (key !== undefined && model.dimension !== 'grain') ctx.onSelect(model.dimension, key);
    },
    selected: ctx.selectedOf(model.dimension).map((k) => ctx.text.valueText(model.dimension, k)),
  };
  const series = group.series.map((s) => ({ key: s.key, label: seriesName(s), values: s.values }));
  const empty = group.series.every((s) => s.values.every((v) => v === null || v === 0));
  if (empty) return <Notice tone="neutral">{t('bi.chart.empty')}</Notice>;
  switch (model.kind) {
    case 'stackedBars':
      return <StackedBarChart {...common} categories={categories} series={series.map((s) => ({ ...s, values: s.values.map((v) => v ?? 0) }))} formatValue={(n) => format(n)} totalLabel={t('bi.chart.total')} formatShare={(f) => fmt.percent(f)} />;
    case 'stackedArea':
      return <StackedAreaChart {...common} labels={categories} series={series.map((s) => ({ ...s, values: s.values.map((v) => v ?? 0) }))} formatValue={(n) => format(n)} {...(model.percent === true ? { percent: true, formatShare: (f: number) => fmt.percent(f) } : { totalLabel: t('bi.chart.total') })} />;
    case 'combo': {
      const lines = model.lines ?? [];
      return (
        <ComboChart
          {...common}
          categories={categories}
          bars={series.filter((s) => !lines.includes(s.key))}
          lines={series.filter((s) => lines.includes(s.key))}
          leftAxisLabel={group.series.filter((s) => !lines.includes(s.key)).map((s) => seriesName(s)).join(', ')}
          rightAxisLabel={group.series.filter((s) => lines.includes(s.key)).map((s) => seriesName(s)).join(', ')}
          formatValue={(n) => format(n, unitOf(group.series.find((s) => !lines.includes(s.key))?.metric ?? null))}
          formatRight={(n) => format(n, (model.lineUnit ?? 'decimal') as BiUnit)}
        />
      );
    }
    default:
      return <ColumnChart {...common} categories={categories} series={series} formatValue={(n) => format(n)} {...(series.length > 1 ? { totalLabel: t('bi.chart.total') } : {})} />;
  }
}

// ───────────────────────────── tables ─────────────────────────────

function Rows({ model, title, ctx }: { model: TableModel; title: string; ctx: InsightContext }): ReactElement {
  const { t, fmt } = ctx.text;
  const labels = useExportLabels();
  if (model.groups.length === 0 || model.groups.every((g) => g.rows.length === 0)) return <Notice tone="neutral">{t('bi.chart.empty')}</Notice>;
  return (
    <Stack gap={4}>
      {model.groups.map((g) => {
        const columns: DataTableColumn[] = model.columns.map((c) => {
          if (c.metric === null && c.key !== 'issue_rate') return { key: c.key, header: ctx.text.dimName(model.dimension) };
          const unit = c.unit as BiUnit;
          const header = c.key === 'issue_rate' ? t('bi.ins.issueRate.col') : ctx.text.metricName(c.metric as string);
          const format = (n: number): string => fmt.chartText(unit, n, g.currency);
          if (unit === 'ratio') return { key: c.key, header, kind: 'heat', format, min: 0, max: 1, align: 'end' };
          return { key: c.key, header, kind: unit === 'count' ? 'bar' : 'number', format, align: 'end' };
        });
        const labelOf = new Map<string, string>();
        const rows: DataTableRow[] = g.rows.map((r) => {
          const label = ctx.text.valueText(model.dimension, r.key, r.label);
          labelOf.set(label, r.key);
          return { key: `${r.key}|${r.marketId ?? ''}`, values: { [model.columns[0]?.key as string]: label, ...r.values } };
        });
        return (
          <DataTable
            key={g.currency ?? 'all'}
            title={g.currency !== undefined && model.groups.length > 1 ? `${title} · ${g.currency}` : title}
            columns={columns}
            rows={rows}
            selectColumn={model.columns[0]?.key as string}
            dimension={model.dimension}
            exportLabels={labels}
            exportName={model.dimension}
            emptyLabel={t('bi.chart.empty')}
            onSelect={(s) => {
              const key = labelOf.get(s.value);
              if (key !== undefined) ctx.onSelect(model.dimension, key);
            }}
            selected={ctx.selectedOf(model.dimension).map((k) => ctx.text.valueText(model.dimension, k))}
          />
        );
      })}
    </Stack>
  );
}

